import type { ReadingManifest } from "./readingTracker";

type Resume = NonNullable<ReadingManifest["resume"]>;
const readerSelector = "[data-qprins-static-document][data-reading-document-id][data-reading-revision-id]";
const initialized = new WeakSet<Document>();

export function initializeReadingGuide(root: Document = document): void {
  if (initialized.has(root)) return;
  initialized.add(root);
  let reader: HTMLElement | undefined;
  let signature = "";
  let manifest: ReadingManifest | undefined;
  let resume: Resume | null = null;
  let marker: HTMLElement | undefined;
  let resumeTarget: HTMLElement | undefined;
  let changed: HTMLElement[] = [];

  function nodeElement(nodeId: string): HTMLElement | null {
    return reader?.querySelector<HTMLElement>(`#node-${CSS.escape(nodeId)}`) ?? null;
  }

  function progressLabel(value: unknown): string {
    return typeof value === "number" && Number.isFinite(value) ? `${Math.floor(Math.max(0, Math.min(100, value)))}%` : "—";
  }

  function resumeExplanation(): string {
    switch (resume?.state) {
      case "changed": return "The paragraph at your previous position has changed.";
      case "nearby": return "Your previous passage was removed. Continue near that position.";
      case "estimated": return "Position estimated from your earlier reading.";
      default: return "Return to your last reading position.";
    }
  }

  function clearResume(): void {
    marker?.remove();
    marker = undefined;
    resumeTarget?.classList.remove("reading-passage--resume");
    resumeTarget = undefined;
  }

  function renderResume(): void {
    const block = resume && manifest?.blocks.find(item => item.nodeId === resume!.nodeId);
    const element = resume ? nodeElement(resume.nodeId) : null;
    if (element && resumeTarget === element && marker?.parentElement === element) return;
    clearResume();
    if (!block?.segments.some(segment => segment.index === resume!.segmentIndex) || !element) return;
    resumeTarget = element;
    resumeTarget.classList.add("reading-passage--resume");
    marker = root.createElement("span");
    marker.className = "reading-resume-marker";
    marker.dataset.readingIgnore = "";
    marker.setAttribute("role", "note");
    marker.setAttribute("aria-label", "Continue reading from this paragraph");
    marker.title = `Continue reading. ${resumeExplanation()}`;
    const label = root.createElement("span");
    label.className = "reading-resume-marker__label";
    label.dataset.readingLabel = "Continue reading";
    label.setAttribute("aria-hidden", "true");
    marker.append(label);
    element.prepend(marker);
  }

  function renderChanges(): void {
    if (!reader || !manifest) return;
    const changes = new Map((manifest.blockChanges ?? []).map(change => [change.nodeId, change.kind]));
    for (const element of reader.querySelectorAll<HTMLElement>("[data-reading-change]")) {
      if (changes.has(element.dataset.nodeId ?? "")) continue;
      delete element.dataset.readingChange;
      element.querySelector(":scope > [data-reading-change-label]")?.remove();
    }
    changed = [];
    for (const block of manifest.blocks) {
      const kind = changes.get(block.nodeId);
      const element = nodeElement(block.nodeId);
      if (!element || (kind !== "new" && kind !== "updated")) continue;
      element.dataset.readingChange = kind;
      let label = element.querySelector<HTMLElement>(":scope > [data-reading-change-label]");
      if (!label) {
        label = root.createElement("span");
        label.className = "reading-passage-label";
        label.dataset.readingChangeLabel = "";
        label.dataset.readingIgnore = "";
        element.prepend(label);
      }
      const text = kind === "new" ? "New · unseen" : "Updated · unseen";
      label.dataset.readingLabel = text;
      label.setAttribute("aria-label", text);
      changed.push(element);
    }
  }

  function updateControls(): void {
    if (!manifest) return;
    const progress = manifest.progress.percentRead;
    root.querySelectorAll<HTMLElement>("[data-reading-guide-value]").forEach(element => {
      const value = progressLabel(progress);
      if (element.textContent !== value) element.textContent = value;
    });
    root.querySelectorAll<HTMLProgressElement>("[data-reading-guide-progress]").forEach(element => {
      element.value = typeof progress === "number" ? progress : 0;
    });
    root.querySelectorAll<HTMLButtonElement>("[data-reading-resume-jump]").forEach(button => {
      button.hidden = !resumeTarget;
      button.disabled = !resumeTarget;
      button.title = resumeExplanation();
    });
    root.querySelectorAll<HTMLElement>("[data-reading-guide-note]").forEach(note => {
      note.hidden = !resumeTarget;
      if (note.textContent !== resumeExplanation()) note.textContent = resumeExplanation();
    });
    root.querySelectorAll<HTMLButtonElement>("[data-reading-changes-jump]").forEach(button => {
      button.hidden = changed.length === 0;
      button.disabled = changed.length === 0;
      button.textContent = `${changed.length} update${changed.length === 1 ? "" : "s"}`;
      button.title = `${changed.length} unseen new or updated passage${changed.length === 1 ? "" : "s"}`;
    });
  }

  root.addEventListener("qprins:reading-manifest", event => {
    const incoming = (event as CustomEvent<ReadingManifest>).detail;
    const element = root.querySelector<HTMLElement>(readerSelector);
    if (!element || incoming?.documentId !== element.dataset.readingDocumentId || incoming.revisionId !== element.dataset.readingRevisionId) return;
    const nextSignature = `${incoming.documentId}/${incoming.revisionId}`;
    if (reader !== element || signature !== nextSignature) {
      clearResume();
      reader = element;
      signature = nextSignature;
      // The arrival bookmark belongs to this visit; reports save the next visit's position separately.
      resume = incoming.resume ? { ...incoming.resume } : null;
    }
    manifest = incoming;
    renderChanges();
    renderResume();
    updateControls();
  });

  root.addEventListener("qprins:reading-mark-status", event => {
    const detail = (event as CustomEvent<{ state: string; documentId?: string; revisionId?: string }>).detail;
    if (detail.state !== "saved" || detail.documentId !== manifest?.documentId || detail.revisionId !== manifest?.revisionId) return;
    resume = null; clearResume(); updateControls();
  });

  function jump(target: HTMLElement): void {
    if (!target.isConnected || !reader || reader.hidden || root.querySelector("#qprins-editor:not([hidden])")) return;
    root.querySelectorAll<HTMLDetailsElement>(".reader-navigation details[open]").forEach(details => { details.open = false; });
    for (let ancestor = target.parentElement; ancestor; ancestor = ancestor.parentElement)
      if (ancestor instanceof HTMLDetailsElement) ancestor.open = true;
    const bounds = target.getBoundingClientRect();
    const header = root.querySelector<HTMLElement>(".workspace-header")?.getBoundingClientRect();
    const clearance = Math.max(0, header && header.top <= 0 ? header.bottom : 0) + 72;
    window.scrollTo({top: window.scrollY + bounds.top - clearance, behavior: "instant"});
    const previousTabIndex = target.getAttribute("tabindex");
    target.tabIndex = -1;
    target.focus({preventScroll: true});
    target.addEventListener("blur", () => {
      if (previousTabIndex === null) target.removeAttribute("tabindex");
      else target.setAttribute("tabindex", previousTabIndex);
    }, {once: true});
  }

  root.addEventListener("click", event => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest("[data-reading-resume-jump]") && resumeTarget) jump(resumeTarget);
    if (target?.closest("[data-reading-changes-jump]") && changed.length) {
      const next = changed.find(element => element.getBoundingClientRect().top > 100) ?? changed[0];
      jump(next);
    }
  });
}
